/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
export default {
    parts: ['swatchGroup', 'swatchLabel', 'swatch', 'swatchesWrapper', 'swatchButton'],
    baseStyle: () => ({
        swatchGroup: {
            flexDirection: 'column '
        },
        swatchLabel: {
            marginBottom: 3
        },
        swatch: {
            position: 'relative',
            backgroundColor: 'stokkeCore.white',
            _focus: {
                outline: 'none',
                boxShadow: 'outline'
            }
        },
        swatchesWrapper: {
            flexWrap: {base: 'nowrap', lg: 'wrap'},
            overflow: 'auto'
        },
        swatchButton: {
            borderColor: 'stokkeGray.mid',
            _disabled: {
                opacity: 1
            }
        }
    }),
    variants: {
        circle: ({selected, disabled, propagateDisabled}) => ({
            swatch: {
                borderRadius: 'full',
                padding: 0,
                cursor: 'pointer',
                color: `${selected ? 'stokkeCore.black' : 'stokkeGray.mid'}`,
                borderColor: `${selected ? 'stokkeCore.black' : 'stokkeGray.mid'}`,
                borderStyle: 'solid',
                _hover: {
                    borderColor: `${selected ? 'stokkeCore.black' : 'stokkeGray.mid'}`,
                    borderStyle: 'solid'
                },
                _active: {
                    background: 'transparent'
                },
                _before: {
                    content: '""',
                    display: `${disabled ? 'block' : 'none'}`,
                    position: 'absolute',
                    zIndex: 1
                }
            },
            swatchButton: {
                borderColor: 'stokkeGray.mid',
                overflow: 'hidden',
                borderRadius: 'full',
                minWidth: 'auto',
                opacity: 1,
                _focus: {
                    outline: 'none'
                },
                pointerEvents: disabled && propagateDisabled ? 'none' : null
            }
        }),
        square: (props) => ({
            swatch: {
                marginRight: 2,
                // diagonal line for disabled options
                // theme variable like stokkeGray.mid won't work inside linear-gradient
                backgroundImage: `${
                    props.disabled
                        ? `${
                              props.selected
                                  ? 'linear-gradient(to top left, transparent calc(50% - 1px), black, transparent calc(50% + 1px) )'
                                  : 'linear-gradient(to top left, white calc(50% - 1px), #c9c9c9, white calc(50% + 1px) )'
                          } `
                        : ''
                }`,
                borderColor: `${props.selected ? 'black' : 'stokkeGray.mid'}`,
                borderStyle: 'solid',
                borderWidth: 1,
                paddingLeft: 3,
                paddingRight: 3,
                marginBottom: 2,
                _focus: {outline: 'none'},
                _hover: {
                    textDecoration: 'none',
                    borderColor: 'gray.900'
                },
                _active: {
                    borderColor: 'gray.900'
                },
                backgroundColor: `${
                    props.selected ? (props.disabled ? 'gray.100' : 'black') : 'white'
                }`,
                color: `${props.selected && !props.disabled ? 'white' : 'gray.900'}`
            },
            swatchButton: {}
        }),
        chip: ({selected, orderable, theme}) => {
            const {textStyles} = theme

            return {
                swatch: {
                    display: 'flex',
                    alignItems: 'center',
                    padding: 'none',
                    borderStyle: orderable ? 'solid' : 'dashed',
                    borderWidth: selected ? '2px' : '1px',
                    borderColor: selected ? 'stokkeCore.black' : 'stokkeGray.mid',
                    borderRadius: 5,
                    paddingLeft: 2,
                    paddingRight: 4,
                    height: 37,
                    ...textStyles['Regular Small'],
                    _hover: {
                        borderWidth: '2px',
                        borderColor: 'stokkeCore.black'
                    },
                    _active: {
                        borderWidth: '2px',
                        borderColor: 'stokkeCore.black'
                    }
                },
                label: {
                    paddingLeft: 2
                },
                swatchButton: {}
            }
        }
    },
    sizes: {
        sm: {
            swatch: {
                minWidth: 6,
                height: 6,
                width: 6,
                borderWidth: 1,
                _before: {
                    width: '1px',
                    height: 8,
                    transform: 'rotate(-45deg)',
                    backgroundColor: 'stokkeCore.black'
                }
            },
            swatchButton: {
                height: 4.5,
                width: 4.5,
                textStyle: 'Badge Regular'
            }
        },
        lg: ({selected}) => ({
            swatch: {
                minWidth: 12,
                height: 12,
                width: 12,
                borderWidth: `${selected ? '2px' : '1px'}`,
                _before: {
                    width: 9.5,
                    height: 9.5,
                    color: 'stokkeCore.black',
                    background: `linear-gradient(to top right,
                         transparent calc(50% - 0.5px),
                         currentColor calc(50% - 0.5px) calc(50% + 0.5px),
                         transparent calc(50% + 0.5px))
                         `
                }
            },
            swatchButton: {
                height: 10,
                width: 10
            }
        })
    },
    defaultProps: {
        variant: 'circle',
        size: 'lg'
    }
}
